import React, { useState, useEffect } from "react";
import { useNavigate, Link } from 'react-router-dom';
import {
  FileText, Eye, Filter, ArrowUp, ArrowDown, Search, Trash2, Clock,
  ChevronLeft, ChevronRight, Plus
} from "lucide-react";
import axios from "axios";

const API_BASE_URL = `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/api`;

const NominationsList = () => {
  const [nominations, setNominations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: "status",
    direction: "asc",
  });
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedNomination, setSelectedNomination] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const navigate = useNavigate();

  const statusOrder = ["pending", "signed", "approved", "rejected", "expired"];

  // Pobieranie nominacji z endpointu
  const fetchNominations = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        throw new Error("No authorization token. Please log in again.");
      }
      const response = await axios.get(`${API_BASE_URL}/nomination/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNominations(response.data);
    } catch (err) {
      console.error("Error fetching nominations:", err);
      const errorMessage = err.response?.data?.error || err.message || "Failed to fetch nominations.";
      setError(errorMessage);
      if (err.response?.status === 401) {
        alert("Session expired. Please log in again.");
        // Optional: Redirect to login page
        // window.location.href = "/login";
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNominations();
  }, []);

  useEffect(() => {
    console.log("Selected Nomination:", selectedNomination);
  }, [selectedNomination]);

  // Reset to first page when items per page changes or when filtered results change
  useEffect(() => {
    const sortedData = getSortedNominations();
    const calculatedTotalPages = Math.ceil(sortedData.length / itemsPerPage) || 1;
    if (currentPage > calculatedTotalPages && calculatedTotalPages > 0) {
      setCurrentPage(1);
    }
  }, [itemsPerPage, nominations.length, filterStatus, searchTerm, currentPage]);

  const getStatusColor = (status) => {
    const colors = {
      pending: "bg-yellow-100 text-yellow-800",
      signed: "bg-lime-100 text-lime-800",
      approved: "bg-green-100 text-green-800",
      rejected: "bg-red-100 text-red-800",
      expired: "bg-steel-100 text-slate-800",
    };
    return colors[status] || "bg-steel-100 text-slate-800";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("pl-PL", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const datePart = date.toLocaleDateString("pl-PL", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    const timePart = date.toLocaleTimeString("pl-PL", {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${datePart} ${timePart}`;
  };


  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
    setCurrentPage(1); // Reset strony przy sortowaniu
  };

  const getShortScopeDisplayName = (scopeName) => {
    const scopeDisplayNames = {
      supervised_planned_maintenance: "Maintenance team member",
      supervised_troubleshooting: "Troubleshooting team member",
      supervising_planned_as_nominated: "Maintenance supervisor",
      supervising_troubleshooting_as_nominated: "Troubleshooting supervisor",
      safe_workplace_preparation: "Work station creator",
    };
    return scopeDisplayNames[scopeName] || scopeName;
  };

  const getFullScopeDisplayName = (scopeName) => {
    const scopeDisplayNames = {
      supervised_planned_maintenance: "Supervised planned maintenance works",
      supervised_troubleshooting: "Supervised troubleshooting",
      supervising_planned_as_nominated:
        "Supervising planned works as nominated person in control of a work activity",
      supervising_troubleshooting_as_nominated:
        "Supervising troubleshooting as nominated person in control of a work activity",
      safe_workplace_preparation:
        "Preparation of safe workplace as person in charge of lockout",
    };
    return scopeDisplayNames[scopeName] || scopeName;
  };

  const getSortedNominations = () => {
    const filteredNominations = nominations
      .filter((nomination) => {
        if (filterStatus === "all") return true;
        return nomination.status === filterStatus;
      })
      .filter((nomination) => {
        if (!searchTerm) return true;
        const searchLower = searchTerm.toLowerCase();
        return (
          nomination.nomination_number?.toLowerCase().includes(searchLower) ||
          nomination.user_name?.toLowerCase().includes(searchLower) ||
          nomination.work_scope_data?.some((scope) =>
            getShortScopeDisplayName(scope.name)
              .toLowerCase()
              .includes(searchLower)
          ) ||
          nomination.company_to_data?.name.toLowerCase().includes(searchLower)
        );
      });

    return filteredNominations.sort((a, b) => {
      if (sortConfig.key === "status") {
        const aStatusIndex = statusOrder.indexOf(a.status);
        const bStatusIndex = statusOrder.indexOf(b.status);
        return (
          (aStatusIndex - bStatusIndex) *
          (sortConfig.direction === "asc" ? 1 : -1)
        );
      } else if (sortConfig.key === "company_to") {
        const aValue = a.company_to_data?.name || "";
        const bValue = b.company_to_data?.name || "";
        return sortConfig.direction === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else if (sortConfig.key === "work_scopes") {
        const aValue = a.work_scope_data
          ?.map((s) => getShortScopeDisplayName(s.name))
          .join(", ");
        const bValue = b.work_scope_data
          ?.map((s) => getShortScopeDisplayName(s.name))
          .join(", ");
        return sortConfig.direction === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else {
        const aValue = a[sortConfig.key] || "";
        const bValue = b[sortConfig.key] || "";
        if (aValue < bValue) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      }
    });
  };

  const handleDelete = async (nominationId) => {
    if (!window.confirm("Are you sure you want to delete this nomination?")) {
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        throw new Error("No authorization token. Please log in again.");
      }
      await axios.delete(`${API_BASE_URL}/nomination/${nominationId}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchNominations();
      setShowDetails(false);
      console.log(`Nomination ${nominationId} deleted successfully`);
    } catch (error) {
      console.error("Error deleting nomination:", error);
      const errorMessage = error.response?.data?.error || "Failed to delete nomination.";
      setError(errorMessage);
      if (error.response?.status === 401) {
        alert("Unauthorized access. Please log in again.");
        // Optional: Redirect to login page
        // window.location.href = "/login";
      } else {
        alert("Failed to delete nomination. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const renderSortIndicator = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === "asc" ? (
      <ArrowUp className="h-4 w-4 ml-1" strokeWidth={1.5} />
    ) : (
      <ArrowDown className="h-4 w-4 ml-1" strokeWidth={2} />
    );
  };

  // Obliczenia paginacji
  const sortedNominations = getSortedNominations();
  const totalPages = Math.ceil(sortedNominations.length / itemsPerPage) || 1;
  const paginatedNominations = sortedNominations.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const getVisiblePages = () => {
    const maxVisiblePages = 5; // Current page + 2 forward + 2 backward
    const pages = [];
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  const NominationDetailsModal = () => {
    useEffect(() => {
      if (!showDetails || !selectedNomination) return;
      const handleKeyDown = (e) => {
        if (e.key === "Escape") {
          setShowDetails(false);
        }
      };
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }, [showDetails, selectedNomination]);

    if (!showDetails || !selectedNomination) return null;

    const requestedCategoryIds = selectedNomination.categories || [];
    const userQualifications = selectedNomination.user_data?.qualifications?.map(
      (q) => q.qualification_type
    ) || [];

    const selectedCategories = selectedNomination.work_scope_data
      ?.flatMap((scope) => scope.categories || [])
      .filter((category) => requestedCategoryIds.includes(category.id)) || [];

    const requiredDocuments = Array.from(
      new Set([
        ...(selectedNomination.work_scope_data?.flatMap((scope) =>
          (scope.required_documents_data || []).map((doc) => JSON.stringify(doc))
        ) || []),
        ...(selectedCategories.flatMap((category) =>
          (category.required_documents_data || []).map((doc) => JSON.stringify(doc))
        ) || []),
      ])
    ).map((doc) => JSON.parse(doc));

    const requiredGroups = selectedCategories
      .filter((cat) => (cat.required_doc_groups_category || []).length > 0)
      .flatMap((cat) => cat.required_doc_groups_category_data || []);

    const canDelete = ["pending", "signed"].includes(selectedNomination.status);

    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
        onClick={() => setShowDetails(false)}
        role="dialog"
        aria-modal="true"
      >
        <div
          className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div className="bg-gradient-to-r from-brand-50 to-steel-50 px-6 py-4 border-b border-steel-200 flex justify-between items-start">
            <div>
              <h3 className="text-xl font-semibold text-slate-900">
                Nomination: {selectedNomination.nomination_number || "N/A"}
              </h3>
              <p className="text-sm text-slate-500 mt-1">
                Created: {selectedNomination.date_create ? formatDateTime(selectedNomination.date_create) : "N/A"}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span
                className={`px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full ${getStatusColor(
                  selectedNomination.status
                )}`}
              >
                {selectedNomination.status || "N/A"}
              </span>
              <button
                onClick={() => setShowDetails(false)}
                className="text-slate-400 hover:text-brand-500 focus:outline-none transition-colors"
                aria-label="Close modal"
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Modal Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-steel-50 rounded-xl p-4">
                <h4 className="text-sm font-semibold text-brand-600 mb-2">Applicant</h4>
                <p className="text-sm text-slate-700">
                  Name: {selectedNomination.company_representative_data || "N/A"}
                </p>
                <p className="text-sm text-slate-700">
                  Company: {selectedNomination.company_data?.name || "N/A"}
                </p>
                <p className="text-sm text-slate-700">
                  Address: {selectedNomination.company_data?.address || "N/A"}
                </p>
                <p className="text-sm text-slate-700">
                  Tax ID: {selectedNomination.company_data?.tax_id || "N/A"}
                </p>
              </div>

              <div className="bg-steel-50 rounded-xl p-4">
                <h4 className="text-sm font-semibold text-brand-600 mb-2">Nominee</h4>
                <p className="text-sm text-slate-700">
                  Name: {selectedNomination.user_name || "Unknown"}
                </p>
                <p className="text-sm text-slate-700">
                  Email: {selectedNomination.user_data?.email || "No email"}
                </p>
                <p className="text-sm text-slate-700">
                  Phone number: {selectedNomination.user_data?.phone_number || "No phone number"}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div className="bg-steel-50 rounded-xl p-4">
                <h4 className="text-sm font-semibold text-brand-600 mb-2">Nomination validity</h4>
                <p className="text-sm text-slate-700">
                  From: {selectedNomination.date_start ? formatDate(selectedNomination.date_start) : "N/A"}{" "}
                  To: {selectedNomination.date_end ? formatDate(selectedNomination.date_end) : "N/A"}
                </p>
                {selectedNomination.status !== "pending" && selectedNomination.date_signed && (
                  <>
                    <h4 className="text-sm font-medium text-slate-500 mt-2">
                      Nomination signed by nominee
                    </h4>
                    <p className="text-sm text-slate-700">
                      Signed: {formatDateTime(selectedNomination.date_signed)}
                    </p>
                  </>
                )}
                {selectedNomination.status === "approved" && selectedNomination.date_approved && selectedNomination.approver_name && (
                  <>
                    <h4 className="text-sm font-medium text-slate-500 mt-2">
                      Nomination approved
                    </h4>
                    <p className="text-sm text-slate-700">
                      By: {selectedNomination.approver_name}
                    </p>
                    <p className="text-sm text-slate-700">
                      Date: {formatDateTime(selectedNomination.date_approved)}
                    </p>
                  </>
                )}
                {selectedNomination.status === "rejected" && selectedNomination.date_rejected && selectedNomination.approver_name && (
                  <>
                    <h4 className="text-sm font-medium text-slate-500 mt-2">
                      Nomination rejected
                    </h4>
                    <p className="text-sm text-slate-700">
                      By: {selectedNomination.approver_name}
                    </p>
                    <p className="text-sm text-slate-700">
                      Date: {formatDateTime(selectedNomination.date_rejected)}
                    </p>
                  </>
                )}
              </div>
              <div className="bg-steel-50 rounded-xl p-4">
                {selectedNomination.status === "rejected" ? (
                  <>
                    <h4 className="text-sm font-semibold text-red-600 mb-2">Rejection reason</h4>
                    <p className="text-sm text-slate-700">
                      {selectedNomination.comment || "No reason provided"}
                    </p>
                  </>
                ) : (
                  <>
                    <h4 className="text-sm font-semibold text-brand-600 mb-2">Authorization level</h4>
                    <div className="mt-1">
                      {selectedNomination.work_scope_data?.map((scope) => (
                        <div key={scope.id} className="mb-2">
                          <p className="text-sm text-slate-700 font-medium">
                            {getFullScopeDisplayName(scope.name)}
                          </p>
                          {(scope.categories || [])
                            .filter((category) => requestedCategoryIds.includes(category.id))
                            .map((category) => (
                              <p key={category.id} className="text-sm text-slate-600 ml-4">
                                - {category.name}
                              </p>
                            ))}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
            {selectedNomination.status !== "rejected" && (
              <div className="mt-4 bg-steel-50 rounded-xl p-4">
                <h4 className="text-sm font-semibold text-brand-600 mb-3">
                  Required documents for this nomination
                </h4>
                <div className="space-y-4">
                  {requiredDocuments.length > 0 && (
                    <div>
                      <h5 className="text-sm font-medium text-slate-700 mb-2">
                        All required documents
                      </h5>
                      <div className="grid grid-cols-2 gap-4 mt-2">
                        {requiredDocuments.map((doc) => {
                          const hasDocument = userQualifications.includes(doc.id);
                          return (
                            <div key={doc.id} className="flex items-center">
                              <input
                                type="checkbox"
                                id={`doc-${doc.id}`}
                                className="mr-2"
                                checked={hasDocument}
                                readOnly
                              />
                              <label
                                htmlFor={`doc-${doc.id}`}
                                className={`text-sm ${hasDocument ? "text-green-600" : "text-red-600"}`}
                              >
                                {doc.name}
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {requiredGroups.length > 0 && (
                    <div>
                      <h5 className="text-sm font-medium text-slate-700 mb-2">
                        Alternative requirement groups (One from each)
                      </h5>
                      {requiredGroups.map((group, groupIndex) => {
                        const groupMet = group.some((doc) =>
                          userQualifications.includes(doc.id)
                        );
                        return (
                          <div
                            key={groupIndex}
                            className={`mt-2 p-3 border border-steel-300 rounded-xl ${groupMet ? "bg-green-100" : "bg-red-100"}`}
                          >
                            <p className="text-sm font-medium text-slate-700">
                              Group {groupIndex + 1}
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-1">
                              {group.map((doc) => {
                                const userHasDocument = userQualifications.includes(doc.id);
                                return (
                                  <div key={doc.id} className="flex items-center">
                                    <input
                                      type="checkbox"
                                      checked={userHasDocument}
                                      disabled
                                      className={`h-4 w-4 rounded border-steel-300 ${userHasDocument ? "text-green-600" : "text-slate-400"}`}
                                    />
                                    <label
                                      className={`text-sm ml-2 ${userHasDocument ? "text-green-700" : "text-slate-700"}`}
                                    >
                                      {doc.name}
                                    </label>
                                  </div>
                                );
                              })}
                            </div>
                            <p
                              className={`text-xs mt-1 ${groupMet ? "text-green-600" : "text-red-600"}`}
                            >
                              {groupMet
                                ? "Requirement met"
                                : "At least one document required"}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {requiredDocuments.length === 0 && requiredGroups.length === 0 && (
                    <p className="text-sm text-slate-500">
                      No requirements defined for the selected scopes and categories.
                    </p>
                  )}
                </div>
              </div>
            )}

          </div>

          {/* Modal Footer */}
          <div className="px-6 py-4 border-t border-steel-200 bg-steel-50 flex justify-end space-x-3">
            <button
              onClick={() => setShowDetails(false)}
              className="px-4 py-2 bg-white border border-steel-300 text-slate-700 rounded-xl hover:bg-steel-100 transition-colors duration-200"
              disabled={loading}
            >
              Close
            </button>
            {canDelete && (
              <button
                onClick={() => handleDelete(selectedNomination.id)}
                className="px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors duration-200 flex items-center"
                disabled={loading}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                {loading ? "Deleting..." : "Delete"}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden h-full">
      <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-steel-200 m-4 flex-1 flex flex-col min-h-0">
        {/* Header */}
        <div className="bg-gradient-to-r from-brand-50 to-steel-50 px-6 py-4 flex justify-between items-center border-b border-steel-200">
          <h2 className="text-2xl font-bold text-slate-900">
            Nominations
          </h2>
          <button
            onClick={() => navigate('/nominations/create', { replace: true })}
            className="bg-brand-500 text-white px-4 py-2 rounded-xl text-sm hover:bg-brand-600 transition-colors duration-300 flex items-center shadow-lg shadow-brand-500/20"
          >
            <Plus size={16} className="mr-2" /> Create new nomination
          </button>
        </div>
        <div className="p-4 flex-1 flex flex-col min-h-0 overflow-hidden">
          {error && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-xl">
              {error}
            </div>
          )}
          <div className="flex flex-col md:flex-row gap-4 mb-6 flex-shrink-0">
            <div className="flex items-center flex-1">
              <div className="relative flex-1">
                <Search className="h-5 w-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search by nomination number, dates, nominee..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-full border border-steel-300 rounded-full focus:outline-none focus:ring-2 focus:ring-brand-500 px-3 py-2"
                  disabled={loading}
                />
              </div>
            </div>
            <div className="flex items-center relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="border border-steel-300 rounded-full pl-10 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500 w-60"
                disabled={loading}
              >
                <option value="all">All status</option>
                <option value="pending">Pending</option>
                <option value="signed">Signed</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="expired">Expired</option>
              </select>
            </div>
          </div>

          <div className="relative flex-1 flex flex-col min-h-0 overflow-hidden">
            {/* Scrollable Grid Table */}
            <div className="flex-1 overflow-y-auto rounded-xl scrollbar-gutter-stable w-full">
              {/* Single Grid for Header and Rows */}
              <div className="grid grid-cols-[minmax(100px,1fr)_minmax(120px,1fr)_minmax(120px,1fr)_minmax(100px,1fr)_minmax(120px,1fr)_minmax(100px,1fr)_minmax(120px,max-content)] w-full">
                {/* Header Row */}
                <div className="grid-table-header contents">
                  <div className="sticky top-0 bg-white z-10 cursor-pointer border-b border-steel-200" onClick={() => handleSort("nomination_number")}>
                    <div className="flex items-center gap-2">
                      Nomination number
                      {renderSortIndicator("nomination_number")}
                    </div>
                  </div>
                  <div className="sticky top-0 bg-white z-10 cursor-pointer border-b border-steel-200" onClick={() => handleSort("date_start")}>
                    <div className="flex items-center gap-2">
                      Validity
                      {renderSortIndicator("date_start")}
                    </div>
                  </div>
                  <div className="sticky top-0 bg-white z-10 cursor-pointer border-b border-steel-200" onClick={() => handleSort("user_name")}>
                    <div className="flex items-center gap-2">
                      Nominee
                      {renderSortIndicator("user_name")}
                    </div>
                  </div>
                  <div className="sticky top-0 bg-white z-10 cursor-pointer border-b border-steel-200" onClick={() => handleSort("company_to")}>
                    <div className="flex items-center gap-2">
                      Company to
                      {renderSortIndicator("company_to")}
                    </div>
                  </div>
                  <div className="sticky top-0 bg-white z-10 cursor-pointer border-b border-steel-200" onClick={() => handleSort("work_scopes")}>
                    <div className="flex items-center gap-2">
                      Authorization level
                      {renderSortIndicator("work_scopes")}
                    </div>
                  </div>
                  <div className="sticky top-0 bg-white z-10 cursor-pointer border-b border-steel-200" onClick={() => handleSort("status")}>
                    <div className="flex items-center gap-2">
                      Status
                      {renderSortIndicator("status")}
                    </div>
                  </div>
                  <div className="sticky top-0 bg-white z-10 text-right border-b border-steel-200">
                    Action
                  </div>
                </div>
                {/* Body Rows */}
                {loading ? (
                  <div className="grid-table-row contents">
                    <div className="col-span-7 text-center py-2">Loading...</div>
                  </div>
                ) : error ? (
                  <div className="grid-table-row contents">
                    <div className="col-span-7 text-center py-2 text-red-600">
                      Error: {error}
                    </div>
                  </div>
                ) : paginatedNominations.length === 0 ? (
                  <div className="grid-table-row contents">
                    <div className="col-span-7 text-center py-2">
                      No nominations to display.
                    </div>
                  </div>
                ) : (
                  paginatedNominations.map((nomination) => (
                    <div
                      key={nomination.id}
                      className={`grid-table-row contents hover:bg-brand-50 cursor-pointer ${
                        selectedNomination && selectedNomination.id === nomination.id
                          ? "bg-brand-100"
                          : ""
                      }`}
                      onClick={() => setSelectedNomination(nomination)}
                      onDoubleClick={() => {
                        setSelectedNomination(nomination);
                        setShowDetails(true);
                      }}
                    >
                      <div className="border-b border-steel-200">
                        <div className="flex items-center">
                          <div className="text-sm font-medium text-[#002D43] truncate">
                            {nomination.nomination_number || "N/A"}
                          </div>
                        </div>
                      </div>
                      <div className="border-b border-steel-200">
                        <div className="flex items-center">
                          <Clock className="h-4 w-4 text-slate-400 mr-1" />
                          <div>
                            <div className="text-sm text-slate-500">
                              {formatDate(nomination.date_start)}
                            </div>
                            <div className="text-sm text-slate-500">
                              to {formatDate(nomination.date_end)}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="border-b border-steel-200">
                        <div className="text-sm text-slate-500">
                          {nomination.user_name || "Unknown"}
                        </div>
                      </div>
                      <div className="text-sm text-slate-500 border-b border-steel-200">
                        {nomination.company_to_data?.name || "N/A"}
                      </div>
                      <div className="border-b border-steel-200">
                        <div className="text-xs text-slate-500">
                          {nomination.work_scope_data?.map((scope) => (
                            <span key={scope.id} className="block truncate">
                              {getShortScopeDisplayName(scope.name)}
                            </span>
                          )) || "No data"}
                        </div>
                      </div>
                      <div className="border-b border-steel-200">
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(
                            nomination.status
                          )}`}
                        >
                          {nomination.status || "N/A"}
                        </span>
                      </div>
                      <div className="text-right border-b border-steel-200">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedNomination(nomination);
                              setShowDetails(true);
                            }}
                            className="p-1.5 bg-steel-100 text-[#002D43] rounded-full hover:bg-steel-200 transition-colors"
                            title="Show details"
                            disabled={loading}
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          {["pending", "signed"].includes(nomination.status) && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(nomination.id);
                              }}
                              className="p-1.5 bg-steel-100 text-red-600 rounded-full hover:bg-steel-200 transition-colors"
                              title="Delete"
                              disabled={loading}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-6 flex-shrink-0">
            <div className="flex items-center gap-4">
              <div className="text-sm text-[#002D43]">
                Showing {(currentPage - 1) * itemsPerPage + 1}-
                {Math.min(currentPage * itemsPerPage, sortedNominations.length)} of {sortedNominations.length} items
              </div>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="text-sm px-4 py-2 border border-steel-300 rounded-full shadow-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black min-w-[140px]"
              >
                <option value={5}>5 per page</option>
                <option value={10}>10 per page</option>
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
              </select>
            </div>
            <div className="flex space-x-2">
              {currentPage > 1 && (
                <button
                  className="px-4 py-2 border border-steel-300 text-[#002D43] rounded-full hover:bg-brand-50 flex items-center justify-center"
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </button>
              )}
              {getVisiblePages().map((page) => (
                <button
                  key={page}
                  className={`px-4 py-2 border ${
                    page === currentPage
                      ? 'border-steel-300 bg-[#E5F7FF] text-black'
                      : 'border-steel-300 text-[#002D43]'
                  } rounded-full hover:bg-brand-50 flex items-center justify-center`}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              ))}
              {currentPage < totalPages && (
                <button
                  className="px-4 py-2 border border-steel-300 text-[#002D43] rounded-full hover:bg-brand-50 flex items-center justify-center"
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <NominationDetailsModal />
    </div>
  );
};

export default NominationsList;